import debounce from 'lodash/debounce'
import first from 'lodash/first'
import includes from 'lodash/includes'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import { ImageVertical } from '@Components/Product/Slider/Components/Image/ImageVertical'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { VideoVertical } from '@Components/Product/Slider/Video'

interface ArrowProps {
  direction: 'up' | 'down'
  disabled?: boolean
  onClick?: () => void
}

const Arrow: FC<ArrowProps> = ({ direction, disabled = false, onClick }) => (
  <button
    css={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(236, 236, 236, 0.8)',
      border: 'none',
      width: '100%',
      height: 27,
      opacity: 0.9,
      '-webkit-box-align': 'center',
      '-webkit-box-pack': 'center',

      '> svg': {
        width: 15,
        height: 'unset',
        transform: `rotate(${direction === 'up' ? -90 : 90}deg)`,
      },

      ':disabled': {
        opacity: 0.3,
      },
    }}
    disabled={disabled}
    onClick={onClick || noop}
    type="button"
  >
    <svg
      focusable="false"
      height="40"
      viewBox="0 0 40 40"
      width="40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11,0L8.4,2.7L26.2,20L8.4,37.3L11,40l20.6-20L11,0z" />
    </svg>
  </button>
)

const VerticalSlider = () => {
  const {
    activeSlide,
    goToNextSlide,
    goToPreviousSlide,
    isFullscreen,
    setActiveSlide,
    slides,
  } = useContext(SliderContext)
  const verticalUL = useRef<HTMLUListElement>(null)
  const [existingFullscreen, setExistingFullscreen] = useState(false)

  useEffect(() => {
    setExistingFullscreen(true)
    setTimeout(() => {
      setExistingFullscreen(false)
    }, 100)
  }, [isFullscreen])

  useEffect(() => {
    const recalculateVerticalTransform = (forceRecalculate = false) => {
      if (!verticalUL.current || (activeSlide.recalculate === false && !forceRecalculate)) {
        return
      }

      const width = verticalUL.current.clientWidth || 0
      const gap = 10
      const height = verticalUL.current.clientHeight || 0
      const parentHeight = verticalUL.current.parentElement?.clientHeight || 0

      if (height === parentHeight) {
        verticalUL.current.style.transform = ''
        return
      }

      const activeSlideStart = (width * activeSlide.key) + Math.max(gap * activeSlide.key, 0)
      const activeSlideCenter = activeSlideStart - (parentHeight / 2) + (width / 2)
      const minDestination = 0
      const maxDestination = height - parentHeight

      const destination = Math.min(
        Math.max(activeSlideCenter, minDestination),
        Math.max(maxDestination, minDestination),
      )

      verticalUL.current.style.transform = `translateY(-${destination}px)`
    }

    recalculateVerticalTransform()

    const debounced = debounce(recalculateVerticalTransform)

    window.addEventListener('resize', () => debounced(true))

    return () => {
      window.removeEventListener('resize', () => debounced(true))
    }
  }, [activeSlide, verticalUL])

  return (
    <div
      css={{
        display: 'none',
        flexDirection: 'column',
        width: 78,
        height: '100%',
        maxHeight: '100%',
        gap: 10,

        '> button': {
          display: slides.length > 5 ? 'block' : 'none',

          '@media (min-width: 900px) and (max-width: 1023px)': {
            display: slides.length > 4 ? 'block' : 'none',
          },
        },

        '@media(min-width: 900px)': {
          display: 'flex',
        },
      }}
    >
      <Arrow
        direction="up"
        disabled={activeSlide.key === 0}
        onClick={() => goToPreviousSlide(true)}
      />
      <div
        css={{
          flexBasis: 0,
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <ul
          css={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            transition: `transform ${existingFullscreen ? '0s' : '.45s'} cubic-bezier(.215, .61,.355, 1)`,
          }}
          ref={verticalUL}
        >
          {map(slides, (slide, key) => (
            // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
            <li
              key={`${slide.url}-${key}`}
              css={{
                width: 78,
                height: 78,
                position: 'relative',
                cursor: 'pointer',

                ':after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  pointerEvents: 'none',
                },
              }}
              onClick={() => setActiveSlide({ key, recalculate: true })}
              onMouseOver={() => setActiveSlide({ key, recalculate: false })}
            >
              <span
                css={{
                  backgroundColor: '#146c92',
                  display: 'block',
                  height: 3,
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: activeSlide.key === key ? 1 : 0,
                  transition: 'opacity .15s ease',
                  zIndex: 100,
                }}
              />
              {
                includes(slide.tags, 'video')
                  ? <VideoVertical asset={first(slides)} />
                  : <ImageVertical asset={slide} index={key} title="TODO" />
              }
            </li>
          ))}
        </ul>
      </div>
      <Arrow
        direction="down"
        disabled={activeSlide.key === slides.length - 1}
        onClick={() => goToNextSlide(true)}
      />
    </div>
  )
}

export default VerticalSlider
